import { useCallback, type RefObject } from 'react';
import { useGcsStore } from '../store/useGcsStore';
import { useSelectedVehicleStore } from '../store/useSelectedVehicleStore';
import { calculateMapPadding } from './useFocusMap';
import type { VehicleId } from '../types/gcs/vehicle';
import { RiskVolume } from '../types/gcs/foreignAircraft';
import type { Location2D } from '../types/gcs/location';
import type { MapRef } from 'react-map-gl/mapbox';

interface Bounds {
  minLng: number;
  maxLng: number;
  minLat: number;
  maxLat: number;
}

/**
 * Custom hook to focus the map on a specific vehicle and its mission context.
 *
 * @param mapRef - Reference to the Mapbox map instance
 * @returns A function to focus on a vehicle given gcsId and vehicleId
 */
export const useFocusVehicle = (mapRef: RefObject<MapRef | null>) => {
  const selectedVehicle = useSelectedVehicleStore((state) => state.selectedVehicle);

  const focusVehicle = useCallback(
    (gcsId: string, vehicleId: VehicleId) => {
      if (!mapRef.current) {
        return;
      }

      const gcsData = useGcsStore.getState().data.get(gcsId);
      if (!gcsData) {
        return;
      }

      // Get the live mission for this vehicle
      const liveMission = gcsData.liveMissionsMap.get(vehicleId);

      // Collect all relevant locations for bounding box
      const locations: Location2D[] = [];

      // Vehicle's launchpad
      const launchpadId = gcsData.vehicleLaunchpadMap.get(vehicleId);
      if (launchpadId) {
        const launchpad = gcsData.launchpads.data?.find((l) => l.id === launchpadId);
        if (launchpad) {
          locations.push(launchpad.location);
        }
      }
      if (liveMission) {
        // Vehicle's current position
        const flightData = gcsData.missionFlightDataMap.get(liveMission.missionId);
        if (flightData?.location) {
          locations.push(flightData.location);
        }

        // Mission route
        const missionRoute = gcsData.missionRouteMap.get(liveMission.missionId);
        if (missionRoute?.route) {
          missionRoute.route.forEach((node) => {
            locations.push(node.location);
          });
        }

        // Delivery point
        locations.push(liveMission.package.order.deliveryPoint.location);

        // Pickup point
        if (liveMission.pickupPoint) {
          locations.push(liveMission.pickupPoint.location);
        }
      }

      // Foreign aircraft with non-nominal risk volume
      gcsData.foreignAircraftDetailsMap.forEach((details, aircraftId) => {
        const riskVolume = gcsData.foreignAircraftRiskVolumeMap.get(aircraftId);
        if (riskVolume && riskVolume.riskVolume !== RiskVolume.NOMINAL) {
          locations.push(details.location);
        }
      });

      // If no locations found, fallback to simple flyTo on vehicle position
      if (locations.length === 0) {
        return;
      }

      // Calculate bounds
      const lngs = locations.map((l) => l.lng);
      const lats = locations.map((l) => l.lat);
      const bounds: Bounds = {
        minLng: Math.min(...lngs),
        maxLng: Math.max(...lngs),
        minLat: Math.min(...lats),
        maxLat: Math.max(...lats)
      };

      // Fit map to bounds with padding
      // Calculate padding dynamically based on panel widths (from CSS variables)
      // and whether the vehicle panel is open
      const padding = calculateMapPadding(!!selectedVehicle);

      mapRef.current.fitBounds(
        [
          [bounds.minLng, bounds.minLat], // Southwest corner
          [bounds.maxLng, bounds.maxLat] // Northeast corner
        ],
        {
          padding,
          duration: 250,
          essential: true
        }
      );
    },
    [mapRef, selectedVehicle]
  );

  return focusVehicle;
};
